/**
 * Leaderboard – filter by exercise (and optional date). Top weights.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getTopLifts } from '../../domain/usecases/leaderboard';
import { userRepo } from '../../data/repositories/UserRepository';
import LeaderboardRow from '../../shared/ui/LeaderboardRow.jsx';
import MedalGlyph from '../../shared/ui/MedalGlyph.jsx';
import EmptyState from '../../shared/ui/EmptyState.jsx';
import Skeleton from '../../shared/ui/Skeleton.jsx';
import Display from '../../shared/ui/Display.jsx';
import styles from './LeaderboardPage.module.css';

const EXERCISES = [
  'Back Squat', 'Front Squat', 'Pause Squat', 'Box Squat',
  'Bench Press', 'Incline Bench Press', 'Close Grip Bench Press',
  'Deadlift', 'Sumo Deadlift', 'Romanian Deadlift', 'Deficit Deadlift',
  'Overhead Press', 'Push Press', 'Push Jerk',
  'Clean', 'Power Clean', 'Clean and Jerk', 'Snatch', 'Hang Snatch',
  'Barbell Row', 'Pull-up', 'Chin-up', 'Lat Pulldown', 'Dumbbell Row',
  'Leg Press', 'Leg Curl', 'Leg Extension', 'Lunges', 'Hip Thrust', 'Good Morning', 'Calf Raise',
  'Barbell Curl', 'Hammer Curl', 'Tricep Pushdown', 'Skull Crusher', 'Dips',
  'Lateral Raise', 'Front Raise', 'Shrugs',
  'Farmers Walk', 'Log Press', 'Other',
];

export function LeaderboardPage() {
  const [exercise, setExercise] = useState('Deadlift');
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getTopLifts(exercise, 50)
      .then((list) => {
        if (!cancelled) setItems(list);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [exercise]);

  useEffect(() => {
    const uids = [...new Set(items.map((w) => w.userId))];
    let cancelled = false;
    (async () => {
      const map = {};
      await Promise.all(
        uids.map(async (uid) => {
          if (cancelled) return;
          const user = await userRepo.getById(uid);
          if (user) map[uid] = user;
        })
      );
      if (!cancelled) setUsers((prev) => ({ ...prev, ...map }));
    })();
    return () => { cancelled = true; };
  }, [items]);

  return (
    <div className={styles.page}>
      <Display as="h1" size="l">Leaderboard</Display>

      <label className={styles.label}>Exercise</label>
      <select
        value={exercise}
        onChange={(e) => setExercise(e.target.value)}
        className={styles.select}
      >
        {EXERCISES.map((ex) => (
          <option key={ex} value={ex}>{ex}</option>
        ))}
      </select>

      {loading ? (
        <div className={styles.skeletonList}>
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} height={56} />)}
        </div>
      ) : items.length === 0 ? (
        <EmptyState icon="trophy" title="No lifts ranked yet" body="Log a lift to appear on the leaderboard." />
      ) : (
        <>
          {items.length >= 3 && (
            <div className={styles.podium}>
              {[items[1], items[0], items[2]].map((entry, i) => {
                const rank = i === 0 ? 'silver' : i === 1 ? 'gold' : 'bronze';
                const displayName = users[entry.userId]?.displayName || 'Athlete';
                return (
                  <Link
                    key={entry.id}
                    to={`/profile/${entry.userId}`}
                    className={styles.podiumCell}
                    data-rank={rank}
                  >
                    <MedalGlyph name="rank" rank={rank} size="lg" />
                    <span className={styles.podiumName}>{displayName}</span>
                    <span className={styles.podiumWeight}>{entry.weight} kg</span>
                    {entry.verified && <span className={styles.verifiedBadge}>Verified</span>}
                  </Link>
                );
              })}
            </div>
          )}

          <div className={styles.rowList}>
            {items.map((workout, i) => {
              const user = users[workout.userId];
              const displayName = user?.displayName || 'Athlete';
              const initials = displayName.split(' ').map((s) => s[0]).slice(0, 2).join('');
              const avatarSrc = user?.avatarUrl ?? user?.photoURL ?? undefined;
              return (
                <Link key={workout.id} to={`/profile/${workout.userId}`} className={styles.rowLink}>
                  <LeaderboardRow
                    rank={i + 1}
                    avatarSrc={avatarSrc}
                    athleteInitials={initials}
                    athleteName={displayName}
                    weight={`${workout.weight} kg`}
                  />
                  {workout.verified ? (
                    <span className={styles.verifiedBadge}>Verified</span>
                  ) : (
                    <span className={styles.pendingBadge}>Pending</span>
                  )}
                </Link>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
